import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/constants/categories';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangDropdownOpen(false);
    setMobileMenuOpen(false);
    if (lng === 'pt') navigate('/');
    else if (lng === 'en') navigate('/en');
    else if (lng === 'de') navigate('/de');
  };

  const currentLang = i18n.language;
  const homePath = currentLang === 'pt' ? '/' : currentLang === 'en' ? '/en' : '/de';

  const navLinks = [
    { label: t('navSpreadsheet'), to: '/cssbuy-spreadsheet', icon: 'ri-grid-line' },
    { label: t('navReview'), to: '/cssbuy-review', icon: 'ri-star-line' },
    { label: t('navShipping'), to: '/cssbuy-shipping', icon: 'ri-truck-line' },
    { label: t('navGuide'), to: '/cssbuy-guide', icon: 'ri-book-open-line' },
    { label: t('navSafety'), to: '/cssbuy-safety', icon: 'ri-shield-check-line' },
    { label: t('navFAQ'), to: '/cssbuy-faq', icon: 'ri-question-line' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 transition-all duration-300 ${
        isScrolled ? 'shadow-md' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to={homePath}
            className="font-heading text-lg md:text-xl text-foreground-950 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            CSSBuy Spreadsheet
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-layout-grid-line" style={{ width: 16, height: 16 }} />
                <span>{t('navCategories')}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} style={{ width: 16, height: 16 }} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50">
                  <div className="px-3 py-1.5 text-xs font-medium text-foreground-400 uppercase tracking-wider">
                    {t('navDropdownCategories')}
                  </div>
                  <div className="grid grid-cols-1 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors"
                      >
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                        />
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className={link.icon} style={{ width: 16, height: 16 }} />
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-1.5">
            <Link
              to={homePath}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line" style={{ width: 16, height: 16 }} />
              <span>{t('navHome')}</span>
            </Link>
            <Link
              to="/cssbuy-guide"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line" style={{ width: 16, height: 16 }} />
              <span>{t('navShoppingGuide')}</span>
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('btnMainSite')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              {t('btnBuyNow')}
            </a>

            <div className="relative ml-1">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1 px-2 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 rounded-lg transition-colors"
              >
                <i className="ri-global-line" style={{ width: 18, height: 18 }} />
                <span className="uppercase text-xs font-semibold">
                  {currentLang === 'pt' ? 'PT' : currentLang === 'en' ? 'EN' : 'DE'}
                </span>
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-1 z-50">
                  <button
                    onClick={() => changeLanguage('pt')}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'pt' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'}`}
                  >
                    {t('navPortuguese')}
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'en' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'}`}
                  >
                    {t('navEnglish')}
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'de' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'}`}
                  >
                    {t('navGerman')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-foreground-800 hover:text-primary-600 transition-colors"
            aria-label={mobileMenuOpen ? t('navClose') : t('navOpen')}
          >
            <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} style={{ width: 24, height: 24 }} />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            {/* Categories grid */}
            <div>
              <p className="px-1 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                {t('navDropdownCategories')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                    />
                    <span className="text-xs font-medium text-foreground-800 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70" />

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-2">
              <Link
                to={homePath}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-background-100 text-foreground-800 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <i className="ri-home-4-line" style={{ width: 16, height: 16 }} />
                {t('navHome')}
              </Link>
              <Link
                to="/cssbuy-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-background-100 text-foreground-800 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <i className="ri-shopping-bag-3-line" style={{ width: 16, height: 16 }} />
                {t('navShoppingGuide')}
              </Link>
            </div>

            {/* Nav links */}
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className={link.icon} style={{ width: 18, height: 18 }} />
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="border-t border-background-200/70" />

            {/* CTA buttons */}
            <div className="flex gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-center bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                {t('btnMainSite')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-center bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
              >
                {t('btnBuyNow')}
              </a>
            </div>

            {/* Language */}
            <div className="flex gap-2">
              <button
                onClick={() => changeLanguage('pt')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'pt' ? 'text-primary-600 bg-primary-50 font-medium' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                {t('navPortuguese')}
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'en' ? 'text-primary-600 bg-primary-50 font-medium' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                {t('navEnglish')}
              </button>
              <button
                onClick={() => changeLanguage('de')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${currentLang === 'de' ? 'text-primary-600 bg-primary-50 font-medium' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                {t('navGerman')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}